/**
 * Alert Card Component
 * Displays a single actionable alert with severity indicator
 */

import { Link } from 'react-router-dom'
import {
  AlertTriangle,
  AlertCircle,
  Info,
  Key,
  Users,
  Briefcase,
  HardDrive,
  ShieldAlert,
  ArrowRight,
} from 'lucide-react'
import type { DashboardAlert, AlertSeverity, AlertType } from '../../types/dashboard'

interface AlertCardProps {
  alert: DashboardAlert
}

const severityStyles: Record<
  AlertSeverity,
  { bg: string; border: string; text: string; icon: string }
> = {
  critical: {
    bg: 'bg-red-50 dark:bg-red-950/30',
    border: 'border-red-200 dark:border-red-900',
    text: 'text-red-700 dark:text-red-400',
    icon: 'text-red-500',
  },
  warning: {
    bg: 'bg-amber-50 dark:bg-amber-950/30',
    border: 'border-amber-200 dark:border-amber-900',
    text: 'text-amber-700 dark:text-amber-400',
    icon: 'text-amber-500',
  },
  info: {
    bg: 'bg-blue-50 dark:bg-blue-950/30',
    border: 'border-blue-200 dark:border-blue-900',
    text: 'text-blue-700 dark:text-blue-400',
    icon: 'text-blue-500',
  },
}

const typeIcons: Record<AlertType, React.ElementType> = {
  license_expiring: Key,
  users_pending_activation: Users,
  cases_overdue: Briefcase,
  cases_critical: Briefcase,
  storage_quota_warning: HardDrive,
  qc_failed: ShieldAlert,
}

const severityIcons: Record<AlertSeverity, React.ElementType> = {
  critical: AlertCircle,
  warning: AlertTriangle,
  info: Info,
}

export function AlertCard({ alert }: AlertCardProps): React.ReactElement {
  const styles = severityStyles[alert.severity]
  const TypeIcon = typeIcons[alert.type]
  const SeverityIcon = severityIcons[alert.severity]

  return (
    <Link
      to={alert.actionPath}
      className={`flex items-center justify-between rounded-lg border p-4 transition-all hover:shadow-md ${styles.bg} ${styles.border}`}
    >
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2">
          <SeverityIcon className={`h-5 w-5 ${styles.icon}`} />
          <TypeIcon className={`h-5 w-5 ${styles.text}`} />
        </div>
        <div>
          <p className={`font-medium ${styles.text}`}>
            {alert.count} {alert.title}
          </p>
          <p className={`text-sm opacity-80 ${styles.text}`}>{alert.actionLabel}</p>
        </div>
      </div>
      <ArrowRight className={`h-5 w-5 ${styles.text}`} />
    </Link>
  )
}
